import { z } from "zod";

import { HORN_IDS, VEHICLE_PRESETS } from "../../types/traffic";
import type {
  GarageApi,
  GarageCustomization,
  VerifiedGaragePurchase,
} from "./GarageApi";

const driverProfileSchema = z.object({
  publicDriverId: z.string().min(8),
  plate: z.string().min(1).max(7),
  vehiclePreset: z.enum(VEHICLE_PRESETS),
  colorId: z.string().min(3).max(24),
  hornId: z.enum(HORN_IDS),
  badge: z.enum(["garage", "founding"]).nullable(),
  garageOwned: z.boolean(),
  odometerMiles: z.number().nonnegative(),
});

const verifySchema = z.object({
  garageOwned: z.literal(true),
  garageKey: z.string().nullable(),
  profile: driverProfileSchema,
}) satisfies z.ZodType<VerifiedGaragePurchase>;

export class GarageApiError extends Error {
  public constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export class NetlifyGarageApi implements GarageApi {
  public async createCheckout(
    customization: GarageCustomization,
  ): Promise<string> {
    const result = z
      .object({ url: z.url() })
      .parse(await this.request("/api/checkout/create", customization));
    return result.url;
  }

  public async verifyPurchase(
    sessionId: string,
  ): Promise<VerifiedGaragePurchase> {
    return verifySchema.parse(
      await this.request(
        `/api/checkout/verify?session_id=${encodeURIComponent(sessionId)}`,
      ),
    );
  }

  public async restore(garageKey: string) {
    const result = z
      .object({ garageOwned: z.literal(true), profile: driverProfileSchema })
      .parse(await this.request("/api/garage/restore", { garageKey }));
    return result.profile;
  }

  public async save(customization: GarageCustomization) {
    const result = z
      .object({ profile: driverProfileSchema })
      .parse(await this.request("/api/garage/save", customization));
    return result.profile;
  }

  public async rotateKey(): Promise<string> {
    const result = z
      .object({ garageKey: z.string().min(16) })
      .parse(await this.request("/api/garage/key/rotate", {}));
    return result.garageKey;
  }

  private async request(path: string, body?: unknown): Promise<unknown> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    try {
      const response = await fetch(path, {
        method: body === undefined ? "GET" : "POST",
        credentials: "same-origin",
        ...(body === undefined
          ? {}
          : {
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
            }),
        signal: controller.signal,
      });
      const value: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        const message = z.object({ error: z.string() }).safeParse(value);
        throw new GarageApiError(
          response.status,
          message.success ? message.data.error : "Garage request failed.",
        );
      }
      return value;
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
